import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// Improved error handling for listing creation
export const createListing = async (listingData: any): Promise<boolean> => {
  try {
    console.log("Creating listing with data:", listingData);
    
    // Validate essential fields to avoid backend errors
    if (!listingData.title) {
      throw new Error("Title is required");
    }
    if (!listingData.description) {
      throw new Error("Description is required");
    }
    if (isNaN(listingData.price) || listingData.price <= 0) {
      throw new Error("Price must be a positive number");
    }
    if (isNaN(listingData.stock) || listingData.stock < 1) {
      throw new Error("Stock must be a positive integer");
    }
    if (!listingData.location) {
      throw new Error("Location is required");
    }
    if (!listingData.phone) {
      throw new Error("Phone number is required");
    }
    
    // If user is not authenticated, user_id must be NULL and contact_email is required
    if (listingData.is_authenticated_listing === false) {
      // CRITICAL: For guest listings, explicitly set user_id to null
      listingData.user_id = null;
      
      // Check for contact email for guest listings
      if (!listingData.contact_email) {
        throw new Error("Email is required for guest listings");
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(listingData.contact_email)) {
        throw new Error("Please provide a valid email address");
      }
    }
    
    console.log("Final listing data being sent:", JSON.stringify(listingData));
    
    // Insert the listing into the database
    const { data, error } = await supabase
      .from("listings")
      .insert(listingData)
      .select();
      
    if (error) {
      console.error("Database error creating listing:", error);
      
      // Handle specific error cases
      if (error.message.includes("violates not-null constraint")) {
        const match = error.message.match(/column "([^"]+)"/);
        const column = match ? match[1] : "unknown";
        throw new Error(`Missing required field: ${column}`);
      } else if (error.message.includes("violates row-level security")) {
        throw new Error("Permission denied: Could not create listing");
      } else {
        throw error;
      }
    }

    console.log("Listing created successfully:", data);
    return true;
  } catch (error: any) {
    console.error("Error in createListing:", error);
    throw error;
  }
};

export const updateListing = async (
  id: number,
  listingData: any
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .update(listingData)
      .eq("id", id);

    if (error) {
      console.error("Error updating listing:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating listing:", error);
    return false;
  }
};

export const deleteListing = async (id: number, userId?: string): Promise<{ success: boolean, error?: string }> => {
  try {
    // First check if the listing has any active orders
    const { data: orders, error: orderError } = await supabase
      .from("orders")
      .select("id")
      .eq("listing_id", id)
      .neq("status", "cancelled");

    if (orderError) {
      console.error("Error checking orders:", orderError);
      return { success: false, error: "Error checking if listing has active orders" };
    }

    // If the listing has active orders, don't allow deletion
    if (orders && orders.length > 0) {
      return { success: false, error: "listing_has_active_orders" };
    }

    // Delete the listing
    const { error } = await supabase
      .from("listings")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting listing:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting listing:", error);
    return { success: false, error: error.message };
  }
};

export const deleteArchivedListing = async (id: number, userId: string): Promise<{ success: boolean, error?: string }> => {
  try {
    // Delete the archived listing
    const { error } = await supabase
      .from("archived_listings")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting archived listing:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting archived listing:", error);
    return { success: false, error: error.message };
  }
};

export const getListingById = async (id: number) => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching listing:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error fetching listing:", error);
    return null;
  }
};

export const approveListing = async (id: number): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .update({ approval_status: "approved", is_active: true })
      .eq("id", id);

    if (error) {
      console.error("Error approving listing:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error approving listing:", error);
    return false;
  }
};

export const rejectListing = async (id: number): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .update({ approval_status: "rejected", is_active: false })
      .eq("id", id);

    if (error) {
      console.error("Error rejecting listing:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error rejecting listing:", error);
    return false;
  }
};

export const toggleListingActivation = async (
  id: number,
  isActive: boolean
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .update({ is_active: isActive })
      .eq("id", id);

    if (error) {
      console.error("Error toggling listing activation:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error toggling listing activation:", error);
    return false;
  }
};

// Add missing functions

export const toggleListingSoldStatus = async (
  id: number, 
  isSold: boolean
): Promise<{ success: boolean, error?: string }> => {
  try {
    const { error } = await supabase
      .from("listings")
      .update({ is_sold: isSold })
      .eq("id", id);

    if (error) {
      console.error("Error toggling sold status:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error toggling sold status:", error);
    return { success: false, error: error.message };
  }
};

export const deactivateListing = async (
  id: number,
  userId: string
): Promise<{ success: boolean, error?: string }> => {
  try {
    const { error } = await supabase
      .from("listings")
      .update({ is_active: false, last_active_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deactivating listing:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error deactivating listing:", error);
    return { success: false, error: error.message };
  }
};

export const reactivateListing = async (
  id: number,
  userId: string
): Promise<{ success: boolean, error?: string }> => {
  try {
    // When reactivating, it should go to 'pending' and use a credit.
    // First, check if user has credits.
    const { data: credits, error: creditsErr } = await supabase.rpc('get_user_credits', { 
      p_user_id: userId 
    });

    if (creditsErr) {
      console.error("Error fetching user credits for reactivation:", creditsErr);
      return { success: false, error: "Failed to check user credits." };
    }
    
    // get_user_credits returns an array
    const userCreditInfo = credits?.[0];

    if (!userCreditInfo || (userCreditInfo.free_listings_remaining <= 0 && userCreditInfo.purchased_listings_credits <= 0)) {
      return { success: false, error: "No credits available to reactivate this listing." };
    }

    // Deduct credit via Edge Function
    const { error: deductError } = await supabase.functions.invoke('deduct-listing-credit', {
      body: { userId: userId }
    });

    if (deductError) {
      console.error("Error deducting credit for reactivation:", deductError);
      // Attempt to parse the error message from the Edge Function if it's a JSON string
      let errorMessage = "Failed to deduct credit.";
      try {
        const parsedError = JSON.parse(deductError.context?.responseText || "{}");
        if (parsedError.error) {
          errorMessage = parsedError.error;
        }
      } catch (e) { /* ignore parsing error */ }
      return { success: false, error: errorMessage };
    }

    // Proceed with reactivation
    const { error } = await supabase
      .from("listings")
      .update({ 
        is_active: true, 
        last_active_at: new Date().toISOString(),
        approval_status: "pending" // Reactivated listings should be re-approved
      })
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error reactivating listing:", error);
      // If reactivation fails after credit deduction, we should ideally refund the credit.
      // This requires calling 'add-listing-credit' Edge Function.
      await supabase.functions.invoke('add-listing-credit', { body: { userId: userId } });
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error reactivating listing:", error);
    return { success: false, error: error.message };
  }
};

export const restoreArchivedListing = async (
  originalId: number, 
  userId: string
): Promise<{ success: boolean, error?: string }> => {
  try {
    // First check if user has any available credits by calling the RPC
    const { data: creditsResult, error: creditsError } = await supabase.rpc('get_user_credits', {
      p_user_id: userId,
    });

    if (creditsError) {
      console.error("Error checking user credits:", creditsError);
      return { success: false, error: "Failed to check available credits" };
    }

    const userCredits = creditsResult?.[0]; // get_user_credits returns an array

    if (!userCredits || (userCredits.free_listings_remaining <= 0 && userCredits.purchased_listings_credits <= 0)) {
      return {
        success: false,
        error: "No credits available to restore this archived listing"
      };
    }

    // Get the archived listing details
    const { data: archivedListing, error: fetchError } = await supabase
      .from("archived_listings")
      .select("*")
      .eq("original_id", originalId)
      .eq("user_id", userId)
      .single(); 

    if (fetchError || !archivedListing) {
      console.error("Error fetching archived listing:", fetchError);
      return { success: false, error: "Archived listing not found or error fetching it." };
    }

    const listingData = {
      ...archivedListing,
      user_id: userId,
      is_active: true, 
      approval_status: "pending", 
      created_at: new Date().toISOString(), 
      updated_at: new Date().toISOString(),
      last_active_at: new Date().toISOString(), 
      is_sold: false, 
    };
    delete (listingData as any).id;
    delete (listingData as any).original_id;
    delete (listingData as any).archived_at;


    const { data: newListing, error: insertError } = await supabase
      .from("listings")
      .insert(listingData)
      .select()
      .single();

    if (insertError) {
      console.error("Error re-inserting archived listing:", insertError);
      return { success: false, error: `Failed to re-insert listing: ${insertError.message}` };
    }

    // Deduct a credit for restoring the listing using the Edge Function
    const { error: deductInvokeError } = await supabase.functions.invoke('deduct-listing-credit', {
        body: { userId: userId }
    });

    if (deductInvokeError) {
        console.error("Error deducting listing credit during restore:", deductInvokeError);
        // Rollback: Delete the newly inserted listing if credit deduction failed
        if (newListing?.id) {
            await supabase.from("listings").delete().eq("id", newListing.id);
        }
        let errorMessage = "Failed to deduct credit for restoration.";
        try {
            const parsedError = JSON.parse(deductInvokeError.context?.responseText || "{}");
            if (parsedError.error) errorMessage = parsedError.error;
        } catch(e) { /* ignore */ }
        return { success: false, error: errorMessage };
    }

    // If re-insertion and credit deduction were successful, delete it from the archived_listings table
    const { error: deleteArchiveError } = await supabase
      .from("archived_listings")
      .delete()
      .eq("id", archivedListing.id) 
      .eq("user_id", userId);

    if (deleteArchiveError) {
      console.error("Error deleting from archived_listings after restore:", deleteArchiveError);
      // Attempt to refund credit and delete the new listing for atomicity
      await supabase.functions.invoke('add-listing-credit', { body: { userId: userId } });
      if (newListing?.id) {
          await supabase.from("listings").delete().eq("id", newListing.id);
      }
      return { success: false, error: `Listing restored, but failed to remove from archive: ${deleteArchiveError.message}. Please contact support.` };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error("Unexpected error in restoreArchivedListing:", error);
    return { success: false, error: error.message || "An unexpected error occurred during restoration." };
  }
};

export const getArchivedListings = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("archived_listings")
      .select(`
        *,
        categories:category_id(name),
        subcategories:subcategory_id(name)
      `)
      .eq("user_id", userId)
      // .eq("is_deleted", false) // Assuming we want to see all archived, even if marked for deletion from archive
      .order("archived_at", { ascending: false });

    if (error) {
      console.error("Error fetching archived listings:", error);
      return [];
    }
    // Ensure data is not null before mapping
    return data ? data.map(item => ({ ...item })) : [];
  } catch (error) {
    console.error("Error fetching archived listings:", error);
    return [];
  }
};

export const getUserListings = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from("listings")
      .select(`
        *,
        categories:category_id(name),
        subcategories:subcategory_id(name)
      `)
      .eq("user_id", userId)
      .eq("is_deleted", false) 
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user listings:", error);
      return [];
    }
     // Ensure data is not null before mapping
    return data ? data.map(item => ({ ...item })) : [];
  } catch (error) {
    console.error("Error fetching user listings:", error);
    return [];
  }
};
